import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Close from "@mui/icons-material/Close";
import {
  Box,
  Divider,
  Card,
  CardContent,
  CardActions,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  MenuItem,
  Select,
} from "@mui/material";
import { Button, Typography, Stack } from "@financeable-com-au/financeable-ui";
import { v4 as uuidv4 } from "uuid";
import {
  organisationSelector,
  inviteUsers,
  getAllOrganisation,
  getOrganisationOwners,
} from "../../store/slices/organisationSlice";
import { userSelector } from "../../store/slices/userSlice";
import regex from "../../utils/regex";
import { saveValue } from "../../store/slices/applicationFormSlice";
import { validateEmail } from "../../store/slices/usersSlice";
import { ERRORS_MESSAGE, SUCCESS_MESSAGE, userType as USER_TYPE } from "../../constants";
import { openSnackbar } from "../../store/slices/snackbarSlice";

export default function InviteUsers() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userId } = useParams();
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState(false);
  const [emailErrorText, setEmailErrorText] = useState("");
  const [userType, setUserType] = useState("");
  const [organisation, setOrganisation] = useState("");
  const [invites, setInvites] = useState([]);
  const [canAdd, setCanAdd] = useState(false);
  const token = localStorage.getItem("token");

  const userState = useSelector(userSelector);
  const { organisationList, gettingOrganisationOwners, organisationOwners } = useSelector(organisationSelector); // inviteUsersSuccess
  const isMasterUser = userState?.userType === USER_TYPE.master;

  const userTypeOptions = ["owner", "admin", "user", "support"];

  const organisationNameList = organisationList?.docs?.map((organisation) => ({
    name: organisation?.name,
    orgId: organisation?._id,
  }));

  const validateEmailAddress = async (em) => {
    if (regex.email.test(em.toLowerCase())) {
      setEmailError(false);
      dispatch(saveValue({ emailInUse: false }));

      setEmailErrorText("");
    } else {
      setEmailError(true);
      setEmailErrorText("Not a valid email address.");
    }

    if (em !== "") {
      await dispatch(validateEmail(email)).unwrap();
    } else {
      setEmailError(true);
      setEmailErrorText("Email is required!");
    }

    if (em && em.length) {
      const emailFound = invites?.find((invite) => invite?.email?.toLowerCase() === em.toLowerCase());
      if (emailFound) {
        setEmailError(true);
        setEmailErrorText("Email already added to List");
        return;
      }
    } else {
    }
  };

  useEffect(() => {
    if (userState?.emailInUse) {
      setEmailError(true);
      return setEmailErrorText("Email address already in use.");
    }

    setEmailError(false);
    return setEmailErrorText("");
  }, [userState?.emailInUse]);

  function handleAddEmail() {
    const uniqueId = uuidv4();
    const findOrg = organisationNameList?.find((org) => organisation === org.name);
    setInvites([...invites, { email, userType, organisation: findOrg, uuid: uniqueId }]);
    setEmail("");
    setUserType("");
    setOrganisation("");
  }

  function handleInviteUsers() {
    const newInvites = invites.map((invite) => ({ ...invite, organisation: invite?.organisation?.orgId }));
    dispatch(
      inviteUsers({
        userId: userId,
        invites: newInvites,
      }),
    ).then((res) => {
      if (res?.payload?.data?.data) {
        setTimeout(() => {
          navigate("/users");
        }, 3000);

        dispatch(
          openSnackbar({
            message: res?.payload?.data?.status_message || SUCCESS_MESSAGE.linkMsg,
            intent: "success",
          }),
        );
      } else {
        dispatch(
          openSnackbar({
            message: res?.payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg,
            intent: "danger",
          }),
        );
      }
    });
  }

  const handle = {
    removeInvitee: (uniqueId) => {
      const inviteList = [...invites];
      const index = inviteList.findIndex((e) => e.uuid === uniqueId);
      inviteList.splice(index, 1);
      setInvites(inviteList);
    },
    getOrganisationId: (organisationName) => {
      return organisationNameList?.find((org) => org.name === organisationName)?.orgId;
    },
    getAllOrganisationOwners: (organisationId) => {
      dispatch(getOrganisationOwners(organisationId)).then((res) => {
        if (res?.error) {
          dispatch(
            openSnackbar({
              message: res?.payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg,
              intent: "danger",
            }),
          );
        }
      });
    },
    onSelectUserType: (event) => {
      setUserType(event.target.value);
      if (organisation && event.target.value === USER_TYPE.owner) {
        const organisationId = handle.getOrganisationId(organisation);
        organisationId && handle.getAllOrganisationOwners(organisationId);
      }
    },
    onSelectOrganisation: (event) => {
      setOrganisation(event.target.value);
      const organisationId = handle.getOrganisationId(event.target.value);
      if (organisationId && userType === USER_TYPE.owner) {
        handle.getAllOrganisationOwners(organisationId);
      }
    },
  };

  useEffect(() => {
    if (email && userType && !emailError) {
      setCanAdd(true);
    } else {
      setCanAdd(false);
    }
  }, [email, emailError, userType]);

  useEffect(() => {
    dispatch(
      getAllOrganisation({
        cursor: "1",
      }),
    );
  }, []);

  return (
    <>
      <Grid container item width="100%" justifyContent="center" alignItems="center" direction="row-reverse">
        <Grid
          container
          item
          xs={12}
          sm={12}
          md={12}
          alignItems="center"
          direction="column"
          textAlign="center"
          sx={{ padding: { xs: "24px 16px", sm: "3vh 24px" } }}
        >
          <Card
            elevation={3}
            sx={{
              maxWidth: 840,
              width: "100%",
              borderRadius: 2,
              textAlign: "left",
              maxHeight: { xs: "calc(100vh - 32px)", sm: "calc(100vh - 120px)" },
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <CardContent
              sx={{
                px: { xs: 2, sm: 4 },
                pt: { xs: 3, sm: 4 },
                pb: 2,
                overflowY: "auto",
                minHeight: 0,
              }}
            >
              <Stack spacing={1} style={{ marginBottom: "30px", textAlign: "center" }}>
                <Typography level="title-lg" fontWeight={800} sx={{ letterSpacing: "-1.8px" }}>
                  Invite colleagues to your Business/Organisation
                </Typography>
                <Typography level="body-md" color="text.secondary" sx={{ fontSize: 16 }}>
                  Invite by adding their work email addresses below.
                </Typography>
              </Stack>

              <Stack spacing={2.5}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={1.5}
                  alignItems={{ xs: "stretch", md: "flex-start" }}
                  flexWrap="wrap"
                  useFlexGap
                >
                  <TextField
                    size="small"
                    label="Email"
                    value={email}
                    sx={{ bgcolor: "background.paper", flex: { md: "1 1 200px" }, minWidth: 0 }}
                    helperText={emailErrorText}
                    error={emailError}
                    onChange={(event) => setEmail(event.target.value)}
                    onBlur={(event) => validateEmailAddress(event.target.value)}
                  />

                  <FormControl sx={{ minWidth: { xs: "100%", md: 140 } }} size="small">
                    <InputLabel id="invite-user-type-label">User Type</InputLabel>
                    <Select
                      labelId="invite-user-type-label"
                      id="invite-user-type"
                      value={userType}
                      label="User Type"
                      onChange={handle.onSelectUserType}
                    >
                      {userTypeOptions.map((item) => (
                        <MenuItem key={item} value={item}>
                          {item.toUpperCase()}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl sx={{ minWidth: { xs: "100%", md: 160 } }} size="small">
                    <InputLabel id="invite-org-label">Organisations</InputLabel>
                    <Select
                      labelId="invite-org-label"
                      id="invite-org"
                      value={organisation}
                      label="Organisations"
                      onChange={handle.onSelectOrganisation}
                    >
                      {organisationNameList?.map((item) => (
                        <MenuItem key={item.orgId} value={item.name}>
                          {item.name.toUpperCase()}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleAddEmail}
                    disabled={!canAdd}
                    sx={{ alignSelf: { xs: "stretch", md: "flex-start" }, minHeight: 40 }}
                  >
                    Add email
                  </Button>
                </Stack>

                {invites?.length > 0 && (
                  <List
                    dense
                    sx={{
                      width: "100%",
                      py: 0,
                      maxHeight: 220,
                      overflowY: "auto",
                      pr: 0.5,
                    }}
                  >
                    {invites.map((invite) => (
                      <ListItem
                        key={invite.uuid}
                        sx={{
                          bgcolor: "action.hover",
                          borderRadius: 1,
                          mb: 1,
                          py: 1.25,
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                        secondaryAction={
                          <IconButton
                            edge="end"
                            aria-label="Remove invite"
                            size="small"
                            onClick={() => handle.removeInvitee(invite.uuid)}
                          >
                            <Close />
                          </IconButton>
                        }
                      >
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", sm: "3fr 1fr 2fr" },
                            gap: { xs: 0.7, sm: 1.5 },
                            width: "100%",
                            pr: 4,
                            alignItems: "center",
                          }}
                        >
                          <Typography sx={{ fontWeight: 500, wordBreak: "break-word" }}>{invite.email}</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ textTransform: "uppercase" }}>
                            {invite.userType}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              textTransform: "uppercase",
                              textAlign: { xs: "left", sm: "right" },
                              wordBreak: "break-word",
                            }}
                          >
                            {invite.organisation?.name}
                          </Typography>
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                )}

                {organisationOwners?.length > 0 && (
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 1,
                      bgcolor: "action.selected",
                      border: "1px solid",
                      borderColor: "divider",
                      maxHeight: 220,
                      overflow: "hidden",
                    }}
                  >
                    <Typography level="title-md" fontWeight={700} sx={{ mb: 2 }}>
                      Already added owners
                    </Typography>
                    <List dense disablePadding sx={{ maxHeight: 160, overflowY: "auto", pr: 0.5 }}>
                      {organisationOwners?.map((owner) => (
                        <ListItem key={owner?._id} disableGutters sx={{ py: 0.25 }}>
                          <Typography level="body-sm">
                            {owner?.firstName || ""} {owner?.lastName || ""}
                          </Typography>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
              </Stack>
            </CardContent>

            <Divider />

            <CardActions
              sx={{
                px: { xs: 2, sm: 4 },
                py: 2.5,
                flexDirection: "column",
                alignItems: "stretch",
                gap: 1,
                bgcolor: "action.hover",
              }}
            >
              <Button
                fullWidth
                disabled={invites?.length <= 0 || gettingOrganisationOwners}
                color="secondary"
                variant="outlined"
                onClick={handleInviteUsers}
              >
                Send invites and continue
              </Button>
              <Button
                fullWidth
                variant="inline"
                color="secondary"
                onClick={() => (!token ? navigate("/applications") : navigate("/users"))}
              >
                {!token ? "Skip" : "Cancel"}
              </Button>
            </CardActions>
          </Card>
        </Grid>
      </Grid>
    </>
  );
}
